import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type LatLngBoundsExpression, type LatLngTuple } from 'leaflet'
import * as React from 'react'
import { useEffect, useRef } from 'react'
import {
	Link,
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { z } from 'zod'
import Breadcrumbs from '#app/components/breadcrumbs.tsx'
import Map from '#app/components/map.tsx'
import PowerSearch from '#app/components/power-search.tsx'
import TrailCards, {
	type TrailWithRequiredCapture,
} from '#app/components/trail-cards.tsx'
import { SimplePagination } from '#app/components/ui/pagination.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { queryCache } from '#app/utils/query-cache.server.ts'
import { CountrySchema, RegionSchema } from '#app/utils/trail-validation.ts'
import { toExplorePath } from '../_to-path.ts'

const PathParamsSchema = z.object({
	country: CountrySchema,
	region: RegionSchema.optional(),
	type: z
		.string()
		.min(2, 'Type must be at least 2 characters long')
		.max(100, 'Type must be at most 100 characters long')
		.optional(),
})

const SearchParamsSchema = z
	.object({
		min: z
			.string()
			.min(1)
			.max(6)
			.default('1000')
			.transform(Number)
			.refine(
				(value) =>
					value === undefined ||
					(!Number.isNaN(value) && value >= 1 && value <= 113000),
				{
					message: 'min must be between 1 and 113000 and cannot result in NaN',
				},
			),
		max: z
			.string()
			.min(1)
			.max(6)
			.default('120000')
			.transform(Number)
			.refine(
				(value) =>
					value === undefined ||
					(!Number.isNaN(value) && value >= 2 && value <= 120000),
				{
					message: 'max must be between 2 and 120000 and cannot result in NaN',
				},
			),
		searchTerm: z.string().default('').optional(),
		tags: z.string().array().optional(),
	})
	.refine(
		({ min, max }) => {
			return max >= min
		},
		{
			message: 'max must be greater than or equal to min',
		},
	)

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const trails = await prisma.trail.findMany({
			select: {
				country: true,
				region: true,
			},
			distinct: ['country', 'region'],
			where: {
				defaultCaptureId: {
					not: null,
				},
			},
		})

		return trails.flatMap((trail) => [
			{
				route: toExplorePath(trail.country),
				priority: 0.7,
			},
			{
				route: toExplorePath(trail.country, trail.region),
				priority: 0.7,
			},
		])
	}),
}

export const meta: MetaFunction<typeof loader> = ({
	loaderData,
	location,
	params,
}) => {
	const { country, region, type } = params
	const parts = [country, region, type].filter(Boolean)
	const title = `Search ${parts.join(' | ')} | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Search ${parts.join(', ')}. View trail details, elevation chart, and interactive 360° panoramas.`

	const { trails = [], currentPage = 1, totalPages = 1 } = loaderData ?? {}

	const pathname = location.pathname
	const url =
		currentPage > 1
			? `https://www.trailviewr.com${pathname}?page=${currentPage}`
			: `https://www.trailviewr.com${pathname}`

	const metadata = [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'ItemList',
				name: `${import.meta.env.VITE_APP_TITLE} Search – ${parts.join(' | ')}`,
				description,
				url,
				itemListElement: trails.map((trail, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					item: {
						'@type': 'TouristAttraction',
						name: trail.name,
						description: trail.details,
						url: `https://www.trailviewr.com/explore/${trail.country}/${trail.region}/trail/${trail.slug}`,
						geo: {
							'@type': 'GeoCoordinates',
							latitude: trail.latitude,
							longitude: trail.longitude,
						},
						address: {
							'@type': 'PostalAddress',
							addressRegion: trail.region,
							addressCountry: trail.country,
						},
					},
				})),
			},
		},
	]

	if (currentPage === 2) {
		metadata.push({
			tagName: 'link',
			rel: 'prefetch prev',
			href: `${pathname}`,
		})
	}

	if (currentPage > 2) {
		metadata.push({
			tagName: 'link',
			rel: 'prefetch prev',
			href: `${pathname}?page=${currentPage - 1}`,
		})
	}

	if (currentPage < totalPages) {
		metadata.push({
			tagName: 'link',
			rel: 'prefetch next',
			href: `${pathname}?page=${currentPage + 1}`,
		})
	}

	return metadata
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	const pathValidationResult = PathParamsSchema.safeParse(params)
	invariantResponse(
		pathValidationResult.success,
		`Invalid path parameters: ${pathValidationResult.error}`,
		{
			status: 400,
		},
	)
	const validPathParams = pathValidationResult.data

	const { country, region, type } = validPathParams

	const requestURL = new URL(request.url)
	const currentPage = Number(requestURL.searchParams.get('page')) || 1
	const itemsPerPage = 6
	const skip = (currentPage - 1) * itemsPerPage

	const searchParams = Object.fromEntries(requestURL.searchParams.entries())

	const selectedTags = requestURL.searchParams.getAll('tags')

	const searchParamsResult = SearchParamsSchema.safeParse({
		...searchParams,
		tags: selectedTags,
	})
	invariantResponse(
		searchParamsResult.success,
		`Invalid query parameters: ${searchParamsResult.error}`,
		{
			status: 400,
		},
	)
	const validSearchParams = searchParamsResult.data

	const { min, max, searchTerm = '', tags = [] } = validSearchParams

	const where_clause: {
		country: string
		length: { gte: number; lte: number }
		region?: string
		type?: string
		name?: { contains: string }
		details?: { contains: string }
		captures?: { some: { tags: { some: { tag: { slug: { in: string[] } } } } } }
	} = {
		country,
		length: { gte: min, lte: max },
	}

	if (region) where_clause.region = region

	const getSearchTermFilter = (searchTerm: string) => {
		const terms = searchTerm
			.split(' ')
			.map((term) => term.trim())
			.filter((term) => term)

		if (terms.length === 0) return {}

		return {
			AND: terms.map((term) => ({
				OR: [{ name: { contains: term } }, { details: { contains: term } }],
			})),
		}
	}

	if (tags.length > 0) {
		where_clause.captures = {
			some: {
				tags: {
					some: {
						tag: {
							slug: { in: tags },
						},
					},
				},
			},
		}
	}

	const cacheKey = `trails-count-${country}-${region || 'all'}-${type || 'all'}-${searchTerm || 'no-search'}-${tags.join(',')}`
	const totalCount = await queryCache.getCachedCount(cacheKey, () =>
		prisma.trail.count({
			where: {
				...where_clause,
				...getSearchTermFilter(searchTerm),
				defaultCaptureId: {
					not: null,
				},
			},
		}),
	)

	const totalPages = Math.ceil(totalCount / itemsPerPage)

	const trails = await prisma.trail.findMany({
		skip,
		take: itemsPerPage,
		select: {
			id: true,
			country: true,
			region: true,
			slug: true,
			name: true,
			details: true,
			length: true,
			elevation_gain: true,
			latitude: true,
			longitude: true,
			defaultCapture: {
				select: {
					id: true,
					slug: true,
					defaultFrameId: true,
					description: true,
					tags: {
						select: {
							tag: {
								select: {
									id: true,
									slug: true,
									name: true,
								},
							},
						},
					},
				},
			},
		},
		where: {
			...where_clause,
			...getSearchTermFilter(searchTerm),
			defaultCaptureId: {
				not: null,
			},
		},
	})

	invariantResponse(
		trails,
		`${Object.keys(where_clause).join('/')} not found`,
		{ status: 404 },
	)

	const validatedTrails = trails.map((trail) => {
		if (trail.defaultCapture == null) {
			throw new Error(
				`Trail ${trail.id} has a null defaultCapture, which should be impossible.`,
			)
		}
		return trail as TrailWithRequiredCapture
	})

	const uniqueCountries = await prisma.trail.findMany({
		distinct: ['country'],
		select: { country: true },
		orderBy: { country: 'asc' },
		take: 50,
	})

	const uniqueRegions = await prisma.trail.findMany({
		distinct: ['region'],
		select: { region: true },
		where: { country: validPathParams.country },
		orderBy: { region: 'asc' },
		take: 50,
	})

	const uniqueTypeSlugs = await prisma.trail.findMany({
		distinct: ['slug'],
		select: {
			name: true,
			slug: true,
			defaultCapture: { select: { slug: true, defaultFrameId: true } },
		},
		where: { country: validPathParams.country, region: validPathParams.region },
		orderBy: { slug: 'asc' },
		take: 12,
	})

	const uniqueTags = await prisma.tag.findMany({
		distinct: ['slug'],
		select: {
			name: true,
			slug: true,
		},
		orderBy: { name: 'asc' },
		take: 100,
	})

	return {
		trails: validatedTrails,
		currentPage,
		totalPages,
		type,
		breadcrumbOptions: {
			country: uniqueCountries.map((trail) => {
				return { name: trail.country, path: trail.country }
			}),
			region: uniqueRegions.map((trail) => {
				return { name: trail.region, path: trail.region }
			}),
			type: [{ name: 'trail', path: 'trail' }],
			trail: uniqueTypeSlugs.map((trail) => {
				return { name: trail.name, path: trail.slug }
			}),
		},
		tags: uniqueTags.map((tag) => ({ value: tag.slug, label: tag.name })),
		search: {
			min: validSearchParams.min,
			max: validSearchParams.max,
			searchTerm: validSearchParams.searchTerm || '',
			tags: validSearchParams.tags || [],
		},
	}
}

export default function CountryRegionTypeRoute() {
	const { country, region, type } = useParams()
	const mapKey = [country, region, type].join('/')
	const [, setSearchParams] = useSearchParams()
	const { trails, breadcrumbOptions, tags, search, currentPage, totalPages } =
		useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const desktopScrollAreaRef = useRef<HTMLDivElement>(null)
	const mobileScrollAreaRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		desktopScrollAreaRef.current?.scrollTo({ top: 0 })
		mobileScrollAreaRef.current?.scrollTo({ top: 0 })
	}, [currentPage])

	function pageChangeHandler(newPage: number): void {
		const params = new URLSearchParams(location.search)
		if (newPage === 1) {
			params.delete('page')
		} else {
			params.set('page', newPage.toString())
		}
		void navigate(
			{ pathname: location.pathname, search: params.toString() },
			{ replace: true },
		)
	}

	function searchOnChangeHandler(search: {
		[key: string]: string | string[]
	}): void {
		const params = new URLSearchParams()
		Object.entries(search).forEach(([key, value]) => {
			if (value === '') return
			if (Array.isArray(value)) {
				value.forEach((element) => params.append(key, element))
			} else {
				params.set(key, value)
			}
		})
		setSearchParams(params)
	}

	const markers = trails.map((trail) => ({
		id: trail.id,
		title: trail.name,
		alt: trail.name,
		position: [trail.latitude, trail.longitude] as LatLngTuple,
		type: 'trail',
		popup: (
			<Link
				to={toExplorePath(trail.country, trail.region, 'trail', trail.slug)}
			>
				{trail.name}
			</Link>
		),
	}))

	const coordinates: LatLngTuple[] = trails.map((trail) => [
		trail.latitude,
		trail.longitude,
	])
	const { center, bounds } = calculateCenterAndBounds(coordinates)

	return (
		<div className="flex flex-1 flex-col overflow-hidden p-1">
			<div className="flex-none">
				<Breadcrumbs
					className="mb-2 sm:mb-4 md:mb-6"
					country={country!}
					region={region!}
					type={type!}
					options={breadcrumbOptions}
				/>
				<PowerSearch
					tagOptions={tags}
					currentSearch={search}
					onChange={searchOnChangeHandler}
				/>
			</div>

			<div className="hidden flex-1 overflow-hidden pt-4 lg:flex">
				<aside className="mr-2 flex h-full w-1/4 max-w-xs flex-col overflow-hidden sm:mr-3 md:mr-4">
					<ScrollArea
						viewportRef={desktopScrollAreaRef}
						className="flex-1 overflow-y-auto"
					>
						{trails.length > 0 ? (
							<>
								<TrailCards trails={trails} />
								<SimplePagination
									className="my-4 md:my-6 lg:my-8 xl:my-10"
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={pageChangeHandler}
								/>
							</>
						) : (
							<div className="mt-4 text-center">
								<div className="font-bold" role="status">
									No trails found...
								</div>
								<div>Please check your entered filters.</div>
							</div>
						)}
					</ScrollArea>
				</aside>
				<main className="h-full grow">
					<Map key={mapKey} center={center} bounds={bounds} markers={markers} />
				</main>
			</div>

			<div className="flex flex-1 flex-col overflow-hidden lg:hidden">
				<Tabs
					defaultValue="list"
					className="flex flex-1 flex-col overflow-hidden"
				>
					<TabsList className="mt-2 flex flex-none justify-between border-t border-gray-700 bg-gray-800 text-white sm:mt-3 md:mt-4">
						<TabsTrigger
							value="list"
							className="flex-1 p-2 text-center sm:p-3 md:p-4"
						>
							List
						</TabsTrigger>
						<TabsTrigger
							value="map"
							className="flex-1 p-2 text-center sm:p-3 md:p-4"
						>
							Map
						</TabsTrigger>
					</TabsList>

					<TabsContent
						value="list"
						className="flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
					>
						<ScrollArea
							viewportRef={mobileScrollAreaRef}
							className="flex-1 overflow-y-auto"
						>
							{trails.length > 0 ? (
								<>
									<TrailCards trails={trails} />
									<SimplePagination
										className="my-4 md:my-6 lg:my-8 xl:my-10"
										currentPage={currentPage}
										totalPages={totalPages}
										onPageChange={pageChangeHandler}
									/>
								</>
							) : (
								<div className="mt-4 text-center">
									<div className="font-bold" role="status">
										No trails found...
									</div>
									<div>Please check your entered filters.</div>
								</div>
							)}
						</ScrollArea>
					</TabsContent>

					<TabsContent
						value="map"
						className="flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
					>
						<div className="flex flex-1">
							<Map
								key={mapKey}
								center={center}
								bounds={bounds}
								markers={markers}
							/>
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}

interface CenterAndBounds {
	center: LatLngTuple
	bounds: LatLngBoundsExpression
}

function calculateCenterAndBounds(coordinates: LatLngTuple[]): CenterAndBounds {
	if (coordinates.length) {
		const initialAccumulator = {
			sumLatitude: 0,
			sumLongitude: 0,
			minimumLatitude: Number.POSITIVE_INFINITY,
			maximumLatitude: Number.NEGATIVE_INFINITY,
			minimumLongitude: Number.POSITIVE_INFINITY,
			maximumLongitude: Number.NEGATIVE_INFINITY,
		}

		const {
			sumLatitude,
			sumLongitude,
			minimumLatitude,
			maximumLatitude,
			minimumLongitude,
			maximumLongitude,
		} = coordinates.reduce((accumulator, coordinate) => {
			return {
				sumLatitude: accumulator.sumLatitude + coordinate[0],
				sumLongitude: accumulator.sumLongitude + coordinate[1],
				minimumLatitude: Math.min(accumulator.minimumLatitude, coordinate[0]),
				maximumLatitude: Math.max(accumulator.maximumLatitude, coordinate[0]),
				minimumLongitude: Math.min(accumulator.minimumLongitude, coordinate[1]),
				maximumLongitude: Math.max(accumulator.maximumLongitude, coordinate[1]),
			}
		}, initialAccumulator)

		const averageLatitude = sumLatitude / coordinates.length
		const averageLongitude = sumLongitude / coordinates.length

		return {
			center: [averageLatitude, averageLongitude],
			bounds: [
				[maximumLatitude, maximumLongitude],
				[minimumLatitude, minimumLongitude],
			],
		}
	}

	return {
		center: [0, 0],
		bounds: [
			[0, 0],
			[0, 0],
		],
	}
}
